import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, Loader2 } from "lucide-react";

// Die schlanke Kundenliste (nur ID, Name, Kundennr.) einmal je Sitzung laden
// und für alle Suchfelder teilen – so ist die Suche in jedem Menübereich
// sofort da, ohne den Server bei jedem Tastendruck zu fragen.
let _cache = null;
let _ladePromise = null;

export async function kundenLaden() {
  if (_cache) return _cache;
  if (!_ladePromise) {
    _ladePromise = api.get("/customers/slim").then((r) => {
      _cache = r.data || [];
      return _cache;
    }).catch(() => { _ladePromise = null; return []; });
  }
  return _ladePromise;
}

/**
 * Globale Kundensuche im Kopfbereich: von jeder Seite aus einen Kunden
 * finden und direkt in dessen Akte springen. Strg+K (oder /) fokussiert.
 */
export default function KundenSuche() {
  const navigate = useNavigate();
  const [begriff, setBegriff] = useState("");
  const [kunden, setKunden] = useState(null);
  const [offen, setOffen] = useState(false);
  const [aktiv, setAktiv] = useState(0);
  const feld = useRef(null);
  const kasten = useRef(null);

  useEffect(() => {
    const taste = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        feld.current?.focus();
      }
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, []);

  // Klick außerhalb schließt die Ergebnisliste
  useEffect(() => {
    const zu = (e) => {
      if (kasten.current && !kasten.current.contains(e.target)) setOffen(false);
    };
    document.addEventListener("mousedown", zu);
    return () => document.removeEventListener("mousedown", zu);
  }, []);

  const oeffnen = async () => {
    setOffen(true);
    if (!kunden) setKunden(await kundenLaden());
  };

  const treffer = useMemo(() => {
    const q = begriff.trim().toLowerCase();
    if (!q || !kunden) return [];
    return kunden
      .filter((k) => (k.name || "").toLowerCase().includes(q)
                  || String(k.kundennr || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [begriff, kunden]);

  const auswaehlen = (k) => {
    setBegriff("");
    setOffen(false);
    navigate(`/kunden/${k.customer_id}`);
  };

  const tasten = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAktiv((a) => Math.min(a + 1, treffer.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAktiv((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && treffer[aktiv]) { e.preventDefault(); auswaehlen(treffer[aktiv]); }
    else if (e.key === "Escape") { setOffen(false); feld.current?.blur(); }
  };

  useEffect(() => { setAktiv(0); }, [begriff]);

  return (
    <div ref={kasten} className="relative w-full" data-testid="global-customer-search">
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        ref={feld}
        value={begriff}
        onChange={(e) => { setBegriff(e.target.value); setOffen(true); }}
        onFocus={oeffnen}
        onKeyDown={tasten}
        placeholder={typeof window !== "undefined" && window.innerWidth < 768
          ? "Kunde suchen…" : "Kunde suchen…  (Strg+K)"}
        data-testid="global-customer-search-input"
        // 16px Schriftgröße auf dem Telefon: alles darunter lässt iOS beim
        // Fokussieren in das Feld hineinzoomen, und die Seite bleibt verschoben.
        className="h-10 md:h-9 w-full rounded-full border border-transparent bg-slate-100 pl-9 pr-3
                   text-base md:text-sm
                   text-slate-900 placeholder:text-slate-400 outline-none transition-all
                   hover:bg-slate-200/60 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/15"
      />
      {offen && begriff.trim() && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md border border-slate-200
                        bg-white shadow-lg overflow-hidden" data-testid="global-customer-search-results">
          {!kunden ? (
            <div className="p-3 flex items-center gap-2 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Kunden werden geladen…
            </div>
          ) : treffer.length === 0 ? (
            <div className="p-3 text-sm text-slate-400">Kein Kunde gefunden.</div>
          ) : (
            treffer.map((k, i) => (
              <button
                key={k.customer_id}
                onMouseDown={(e) => { e.preventDefault(); auswaehlen(k); }}
                onMouseEnter={() => setAktiv(i)}
                data-testid={`global-search-result-${k.customer_id}`}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3
                            ${i === aktiv ? "bg-slate-100" : "bg-white"}`}
              >
                <span className="font-medium text-slate-900 truncate">{k.name}</span>
                {k.kundennr && <span className="text-xs text-slate-400 tabular shrink-0">Nr. {k.kundennr}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
